import { useParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { VenueAvailabilities, VenueAvailabilitiesVariables } from './__queries__';
import { useConfig } from 'Components/ConfigProvider';
import { DateTime } from 'luxon';
import useI18n from 'i18n';

const VENUE_QUERY = gql`
  query VenueAvailabilities($id: ID!) {
    venue: Venue(id: $id) {
      id
      name
      availabilities {
        id
        dayOfWeek
        timeRange {
          id
          start
          end
        }
      }
      pauseOnlineOrdersUntil
      minimumPickupWaitTime
    }
  }
`;

enum DAY_OF_WEEK {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

const FALLBACK_CLOSED = {
  isOpen: false,
  from: '--',
  to: '--',
};

type Availability = {
  id: string;
  dayOfWeek: DAY_OF_WEEK;
  timeRange: {
    id: string;
    start: string;
    end: string;
    initialStart?: string;
    initialEnd?: string;
    isMidnight?: boolean;
  };
};

const useOpeningHours = (selectedVenueId?: string) => {
  const config = useConfig();
  const { i18n } = useI18n();
  const params = useParams<{ venue_id?: string }>();
  const venueId = selectedVenueId || params?.venue_id || '';
  const now = DateTime.now();

  const venueQuery = useQuery<VenueAvailabilities, VenueAvailabilitiesVariables>(VENUE_QUERY, {
    variables: {
      id: venueId,
    },
    skip: !venueId,
  });

  const minimumPickupWaitTime = venueQuery.data?.venue.minimumPickupWaitTime;
  const pauseOnlineOrdersUntil = venueQuery.data?.venue.pauseOnlineOrdersUntil;

  const pausedUntilUtc = pauseOnlineOrdersUntil
    ? DateTime.fromISO(pauseOnlineOrdersUntil, { zone: 'utc' })
    : null;

  const isOrdersPaused = pauseOnlineOrdersUntil != null;

  // Convert to local time so consumers can compare with local opening hours
  const pausedUntilLocal = pausedUntilUtc?.toLocal() ?? null;

  // The reason for this function is that it is difficult to check if a time is within timerange if the timerange exceeds midnight.
  // To solve this difficulty, the function splits the range // into new objects so that no individual range spans past midnight.
  // Example: start: 12:00 - 03:00 -> {start: 12:00, initialEnd: 03:00, end: 00:00}, {initialStart: 12:00, start: 00:00, end: 03:00}
  const splitOverMidnightAvailability = (availabilities: Availability[]) => {
    return availabilities.flatMap((availability) => {
      const startToUse = availability.timeRange.start;
      const endToUse = availability.timeRange.end;

      const start = DateTime.fromISO(startToUse);

      // If endTime is 00:00 we convert it to the last millisecond of the day for comparison
      const end =
        endToUse === '00:00' ? DateTime.fromISO('23:59:59:999') : DateTime.fromISO(endToUse);

      // If the end is 00:00 we don't need to split it into multiple parts
      if (endToUse === '00:00') {
        return [
          {
            ...availability,
            timeRange: {
              id: availability.timeRange.id,
              start: startToUse,
              end: endToUse,
            },
          } as Availability,
        ];
      }

      // If the availability does not span midnight, return it as is
      if (start < end) {
        return [
          {
            ...availability,
            timeRange: {
              id: availability.timeRange.id,
              start: startToUse,
              end: endToUse,
            },
          } as Availability,
        ];
      }

      // Split into two availabilities when spanning over midnight
      const nextDay = getNextDay(availability.dayOfWeek);

      return [
        {
          // First part: from start to 00:00 on the current day
          ...availability,
          timeRange: {
            id: availability.timeRange.id,
            start: startToUse,
            initialEnd: endToUse, // Initial end is saved for display purposes so we can display 12:00 - 03:00 instead of 12:00 - 00:00 and 00:00 - 03:00
            end: '00:00',
          },
        },
        {
          // Second part: from 00:00 to end on the next day
          ...availability,
          dayOfWeek: nextDay, // Change the day to the next day
          timeRange: {
            id: availability.timeRange.id,
            initialStart: startToUse, // Initial start is saved for display purposes so we can display 12:00 - 03:00 instead of 12:00 - 00:00 and 00:00 - 03:00
            start: '00:00',
            end: endToUse,
            isMidnight: true,
          },
        },
      ];
    });
  };

  // Helper function to get the next day of the week
  const getNextDay = (currentDay: DAY_OF_WEEK): DAY_OF_WEEK => {
    const daysOfWeek = Object.values(DAY_OF_WEEK);
    const currentIndex = daysOfWeek.indexOf(currentDay);
    if (currentIndex === -1) {
      throw new Error(`Invalid day of the week: ${currentDay}`);
    }

    const nextIndex = (currentIndex + 1) % daysOfWeek.length; // Wrap around if necessary (SUNDAY after SATURDAY)
    return daysOfWeek[nextIndex];
  };

  const availabilities = splitOverMidnightAvailability(
    venueQuery.data?.venue.availabilities ?? [],
  );

  const getCorrectAvailability = (availabilitiesList: Availability[], date: DateTime) => {
    // Normalize the input date (just keep the time part for comparison, set year/month/day to 1970-01-01)
    const timeOnly = date.set({ year: 1970, month: 1, day: 1, second: 1 });

    // Online ordering should show "Closed" at their closing time MINUS cooking time. To avoid getting orders too late
    // Example: Opening hours set to 10-21. If a customer goes into the app at 20.45 and the "cooking time" is set to 15 min it should show "Closed".
    // It should not be possible to order at 20.59 for example
    let waitTime = 15;

    if (config.SYNC_ETA) {
      waitTime = minimumPickupWaitTime ?? 15;
    }

    const isOpen = (
      startTime: DateTime,
      endTime: DateTime,
      availability: Availability,
      isMidnight: boolean,
    ) => {
      const { timeRange } = availability;

      // Subtract minimumPickupWaitTime to avoid receiving an order if the preparation time is longer than the restaurant's opening hours.
      const correctedEndTime = endTime.minus({ minutes: waitTime });

      // Normalize start and end times for the same day (1970-01-01)
      const normalizedStartTime = startTime.set({ year: 1970, month: 1, day: 1 });
      let normalizedEndTime = correctedEndTime.set({ year: 1970, month: 1, day: 1 });

      // Add 1 day to midnight for checking because it's the next day
      if (isMidnight) {
        normalizedEndTime = normalizedEndTime.plus({ day: 1 });
      }

      if (normalizedStartTime < normalizedEndTime) {
        if (timeOnly >= normalizedStartTime && timeOnly <= normalizedEndTime) {
          return {
            isOpen: true,
            from: timeRange.initialStart ?? timeRange.start,
            to: timeRange.initialEnd ?? timeRange.end,
          };
        }
      }

      return null;
    };

    for (const availability of availabilitiesList) {
      const { timeRange } = availability;

      // Extracting time from the start and end strings
      const startTime = DateTime.fromISO(timeRange.start);
      // If endTime is 00:00 we convert it to the last second of the day for comparison
      const endTime =
        timeRange.end === '00:00' ? DateTime.fromISO('23:59:59') : DateTime.fromISO(timeRange.end);

      const opened = isOpen(startTime, endTime, availability, false);
      if (opened) {
        return opened;
      }
    }

    // Check the nighttime
    const midnightAvailability = availabilities.find(
      (a) => a.id === availabilitiesList[0]?.id && a.timeRange.isMidnight,
    );

    // Example: Venue opening hours: 12:00-03:00, minimumPickupWaitTime = 10 min. It's 2 days: 12:00-00:00 / 00:00-03:00
    // For this reason we will check 12:00-03:00 and use initialStart. Otherwise, Venue will close at 23:50 and then at 02:50
    if (midnightAvailability) {
      const startTime = DateTime.fromISO(midnightAvailability.timeRange.initialStart!);
      const endTime = DateTime.fromISO(midnightAvailability.timeRange.end);
      const opened = isOpen(startTime, endTime, midnightAvailability, true);

      if (opened) {
        return opened;
      }
    }

    return null;
  };

  const checkOpeningHoursForDate = (date?: Date) => {
    const timeForComparison = !date ? now : DateTime.fromJSDate(date);

    if (!timeForComparison || !timeForComparison.isValid) {
      return FALLBACK_CLOSED;
    }

    // Reason we use en-GB is because we save the day as english example WEDNESDAY
    const selectedDay = timeForComparison.setLocale('en-GB').weekdayLong.toUpperCase();
    const foundDayAvailabilities = availabilities.filter((a) => a.dayOfWeek === selectedDay);

    const availabilityResult = getCorrectAvailability(foundDayAvailabilities, timeForComparison);

    // If a matching availability was found, return it with all time ranges for the day
    if (availabilityResult) {
      // Filter out midnight availabilities and get unique time ranges for display
      const regularAvailabilities = foundDayAvailabilities.filter(
        (a) =>
          !a.timeRange.isMidnight &&
          !(a.timeRange.start === '00:00' && a.timeRange.end === '00:00'),
      );

      if (regularAvailabilities.length > 1) {
        const timeRanges = regularAvailabilities
          .map((a) => {
            const from = a.timeRange.initialStart || a.timeRange.start;
            const to = a.timeRange.initialEnd || a.timeRange.end;
            return `${from}-${to}`;
          })
          .join(', ');

        return {
          isOpen: true,
          from: timeRanges,
          to: '', // Empty to indicate it's a combined display
        };
      }

      return availabilityResult;
    }

    // If the venue is closed at the selected time, display opening hours for the day
    // Filter out midnight availabilities and get unique time ranges
    const regularAvailabilities = foundDayAvailabilities.filter(
      (a) =>
        !a.timeRange.isMidnight && !(a.timeRange.start === '00:00' && a.timeRange.end === '00:00'),
    );

    if (regularAvailabilities.length === 0) {
      return {
        isOpen: false,
        from: '--',
        to: '--',
      };
    }

    // For multiple availability periods (e.g., 10-14, 16-22), show all periods. This values are not displayed in the UI, so these can be tweaked as needed in the future.
    if (regularAvailabilities.length > 1) {
      const timeRanges = regularAvailabilities
        .map((a) => {
          const from = a.timeRange.initialStart || a.timeRange.start;
          const to = a.timeRange.initialEnd || a.timeRange.end;
          return `${from}-${to}`;
        })
        .join(', ');

      return {
        isOpen: false,
        from: timeRanges,
        to: '', // Empty to indicate it's a combined display
      };
    }

    // Single availability period
    const availability = regularAvailabilities[0];
    return {
      isOpen: false,
      from: availability?.timeRange.initialStart || availability?.timeRange.start || '--',
      to: availability?.timeRange.initialEnd || availability?.timeRange.end || '--',
    };
  };

  const getNextOpeningTime = (): DateTime | null => {
    const currentTime = now;
    let foundOpeningTime: DateTime | null = null;

    for (let i = 0; i < WEEK_DAYS.length; i++) {
      const dayIndex = (currentTime.weekday + i - 1) % WEEK_DAYS.length; // Offset by current day and wrap around
      const dayOfWeek = WEEK_DAYS[dayIndex];

      const foundDayAvailabilities = availabilities.filter(
        (availability) => availability.dayOfWeek === dayOfWeek,
      );

      for (const availability of foundDayAvailabilities) {
        const openingTime = DateTime.fromISO(availability.timeRange.start).set({
          year: currentTime.year,
          month: currentTime.month,
          day: currentTime.day + i,
        });

        // If it's today and opening time is later than now, or if it's a future day
        if (openingTime > currentTime) {
          foundOpeningTime = openingTime;
          break;
        }
      }

      if (foundOpeningTime) break; // Exit loop if we found an opening time
    }

    return foundOpeningTime;
  };

  const WEEK_DAYS = [
    DAY_OF_WEEK.MONDAY,
    DAY_OF_WEEK.TUESDAY,
    DAY_OF_WEEK.WEDNESDAY,
    DAY_OF_WEEK.THURSDAY,
    DAY_OF_WEEK.FRIDAY,
    DAY_OF_WEEK.SATURDAY,
    DAY_OF_WEEK.SUNDAY,
  ];

  const openingHoursList = WEEK_DAYS.map((day) => {
    const foundDayAvailabilities = venueQuery.data?.venue.availabilities.filter(
      (a) => a.dayOfWeek === day,
    );

    if (!foundDayAvailabilities || foundDayAvailabilities.length === 0) {
      return {
        day: i18n('Weekday.' + day),
        openingHours: i18n('Store.Closed'),
      };
    }

    // Handle multiple time ranges per day
    const timeRanges = foundDayAvailabilities
      .map((availability) => {
        const start = availability.timeRange.start;
        const end = availability.timeRange.end;

        // Handle 24h opening hours
        if (start === '00:00' && end === '00:00') {
          return '24h';
        }

        return start && end ? `${start} - ${end}` : null;
      })
      .filter(Boolean);

    return {
      day: i18n('Weekday.' + day),
      openingHours: timeRanges.length > 0 ? timeRanges.join(', ') : i18n('Store.Closed'),
    };
  });

  return {
    today: checkOpeningHoursForDate(),
    disablePreOrder: config.DISABLE_PICKUP_TIME,
    loading: !venueQuery.data || venueQuery.loading,
    openingHoursList: openingHoursList,
    checkOpeningHoursForDate,
    getNextOpeningTime,
    isOrdersPaused,
    pausedUntilLocal,
  };
};

export default useOpeningHours;
